import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faAward, faCrown } from '@fortawesome/free-solid-svg-icons';
import { ErrorMessage } from './CommonUI';
import axios from 'axios';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// ─── Rarity helpers (mirrors UserSetsView) ────────────────────────────────

const getRarityIcon = (rarity) => {
    switch (rarity) {
        case 'mythical': return faCrown;
        case 'rare':     return faAward;
        case 'common':
        default:         return faTrophy;
    }
};

const getRarityGradient = (rarity) => {
    switch (rarity) {
        case 'mythical': return 'from-purple-500 to-purple-600 border-purple-400';
        case 'rare':     return 'from-blue-500 to-blue-600 border-blue-400';
        case 'common':
        default:         return 'from-amber-500 to-amber-600 border-amber-400';
    }
};

const TrophyIcon = ({ trophy, size = 'md' }) => {
    const dim   = size === 'sm' ? 'w-8 h-8'   : 'w-11 h-11';
    const text  = size === 'sm' ? 'text-base'  : 'text-lg';
    const round = size === 'sm' ? 'rounded-full' : 'rounded-lg';
    const grad  = getRarityGradient(trophy.rarity);
    const icon  = getRarityIcon(trophy.rarity);

    if (trophy.image_path) {
        return (
            <div className={`${dim} ${round} flex-shrink-0 overflow-hidden`}>
                <img
                    src={trophy.image_path}
                    alt={trophy.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.removeProperty('display');
                    }}
                />
                <div
                    className={`${dim} ${round} bg-gradient-to-br ${grad} border-2 items-center justify-center hidden`}
                >
                    <FontAwesomeIcon icon={icon} className={`${text} text-white`} />
                </div>
            </div>
        );
    }

    return (
        <div className={`${dim} ${round} flex-shrink-0 bg-gradient-to-br ${grad} border-2 flex items-center justify-center`}>
            <FontAwesomeIcon icon={icon} className={`${text} text-white`} />
        </div>
    );
};

const AdminTrophies = () => {
    const { user, isLoading: authLoading, checkAdminStatus } = useAuth();
    const navigate = useNavigate();
    useDocumentTitle('Admin Trophies');

    const [isLoading,    setIsLoading]    = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [trophies,     setTrophies]     = useState([]);
    const [filters,      setFilters]      = useState({ rarity: '', search: '' });
    const [pagination,   setPagination]   = useState({
        current_page: 1, total_pages: 1, total_trophies: 0, trophies_per_page: 50,
    });
    const [showEditModal,   setShowEditModal]   = useState(false);
    const [editingTrophy,   setEditingTrophy]   = useState(null);
    const [formData,        setFormData]        = useState({ name: '', description: '', image_url: '', rarity: 'common' });
    const [isSaving,        setIsSaving]        = useState(false);
    const [error,           setError]           = useState(null);
    const [showAssignModal,     setShowAssignModal]     = useState(false);
    const [assigningTrophy,     setAssigningTrophy]     = useState(null);
    const [users,               setUsers]               = useState([]);
    const [userSearch,          setUserSearch]          = useState('');
    const [showRecipientsModal, setShowRecipientsModal] = useState(false);
    const [recipientsTrophy,    setRecipientsTrophy]    = useState(null);
    const [recipients,          setRecipients]          = useState([]);
    const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);

    // ── Auth ──────────────────────────────────────────────────────────────
    useEffect(() => {
        const verifyAdminAccess = async () => {
            if (authLoading) return;
            if (!user) { navigate('/'); return; }
            const isAdmin = await checkAdminStatus();
            if (!isAdmin) { navigate('/'); return; }
            setIsAuthorized(true);
            setIsLoading(false);
            await loadTrophiesData();
        };
        verifyAdminAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading, checkAdminStatus, navigate]);

    useEffect(() => {
        if (isAuthorized) loadTrophiesData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, pagination.current_page, isAuthorized]);

    // ── Data loading ──────────────────────────────────────────────────────
    const loadTrophiesData = async () => {
        try {
            setError(null);
            const params = new URLSearchParams();
            if (filters.rarity)  params.append('rarity',  filters.rarity);
            if (filters.search)  params.append('search',  filters.search);
            params.append('page',  pagination.current_page);
            params.append('limit', pagination.trophies_per_page);

            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_get_trophies.php?${params}`, {
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Failed to fetch trophies data');
            const data = await res.json();
            if (!data.success) throw new Error(data.message || 'Failed to load trophies');

            setTrophies(data.trophies);
            setPagination(data.pagination);
        } catch (e) {
            setError(`Unable to load trophies data: ${e.message}`);
        }
    };

    // ── Edit / create modal ───────────────────────────────────────────────
    const handleEditTrophy = (trophy = null) => {
        setEditingTrophy(trophy);
        setFormData(trophy ? {
            name:        trophy.name,
            description: trophy.description,
            image_url:   trophy.image_path || '',
            rarity:      trophy.rarity,
        } : { name: '', description: '', image_url: '', rarity: 'common' });
        setShowEditModal(true);
    };

    const handleSaveTrophy = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);
        try {
            const submitData = { ...formData, ...(editingTrophy && { id: editingTrophy.id }) };
            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_save_trophy.php`, {
                method:      'POST',
                credentials: 'include',
                headers:     { 'Content-Type': 'application/json' },
                body:        JSON.stringify(submitData),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Failed to save trophy');
            setShowEditModal(false);
            setEditingTrophy(null);
            await loadTrophiesData();
        } catch (e) {
            setError(`Failed to save trophy: ${e.message}`);
        }
        setIsSaving(false);
    };

    // ── Delete ────────────────────────────────────────────────────────────
    const handleDeleteTrophy = async (trophyId) => {
        if (!window.confirm('Delete this trophy? This will also remove it from all users who have it.')) return;
        try {
            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_delete_trophy.php`, {
                method:      'POST',
                credentials: 'include',
                headers:     { 'Content-Type': 'application/json' },
                body:        JSON.stringify({ id: trophyId }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Failed to delete trophy');
            await loadTrophiesData();
        } catch (e) {
            setError(`Failed to delete trophy: ${e.message}`);
        }
    };

    // ── Assign modal ──────────────────────────────────────────────────────
    const handleAssignTrophy = async (trophy) => {
        setAssigningTrophy(trophy);
        setShowAssignModal(true);
        setUserSearch('');
        try {
            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_get_users.php?limit=100`, {
                credentials: 'include',
            });
            const data = await res.json();
            if (data.success) setUsers(data.users);
        } catch (e) {
            setError(`Failed to load users: ${e.message}`);
        }
    };

    const handleAssignTrophyToUser = async (userId) => {
        if (!assigningTrophy) return;
        try {
            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_assign_trophy.php`, {
                method:      'POST',
                credentials: 'include',
                headers:     { 'Content-Type': 'application/json' },
                body:        JSON.stringify({ user_id: userId, trophy_id: assigningTrophy.id }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Failed to assign trophy');
            setShowAssignModal(false);
            setAssigningTrophy(null);
            await loadTrophiesData();
        } catch (e) {
            setError(`Failed to assign trophy: ${e.message}`);
        }
    };

    // ── Recipients modal ─────────────────────────────────────────────────
    const handleViewRecipients = async (trophy) => {
        setRecipientsTrophy(trophy);
        setShowRecipientsModal(true);
        setIsLoadingRecipients(true);
        setRecipients([]);
        try {
            const res  = await fetch(
                `${process.env.REACT_APP_API_URL}/admin_get_trophy_recipients.php?trophy_id=${trophy.id}`,
                { credentials: 'include' }
            );
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Failed to load recipients');
            setRecipients(data.recipients);
        } catch (e) {
            setError(`Failed to load recipients: ${e.message}`);
            setShowRecipientsModal(false);
        } finally {
            setIsLoadingRecipients(false);
        }
    };

    // ── Image upload ──────────────────────────────────────────────────────
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowed.includes(file.type)) {
            setError('Invalid file type. Please upload JPEG, PNG, GIF, WebP, or SVG.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB.`);
            return;
        }
        try {
            const fd = new FormData();
            fd.append('image', file);
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/trophy_upload_image.php`,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
            );
            if (!res.data.success) throw new Error(res.data.message || 'Upload failed');
            setFormData(prev => ({ ...prev, image_url: res.data.image.url }));
        } catch (e) {
            setError(`Failed to upload image: ${e.response?.data?.error || e.message}`);
        }
    };

    // ── Helpers ───────────────────────────────────────────────────────────
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const getRarityColor = (rarity) => {
        switch (rarity) {
            case 'mythical': return 'text-purple-700 bg-purple-100 border-purple-200';
            case 'rare':     return 'text-blue-700 bg-blue-100 border-blue-200';
            case 'common':   return 'text-green-700 bg-green-100 border-green-200';
            default:         return 'text-gray-600 bg-gray-100 border-gray-200';
        }
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

    const filteredUsers = users.filter(u =>
        !userSearch ||
        u.username.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
        (u.display_name && u.display_name.toLowerCase().includes(userSearch.toLowerCase()))
    );

    // ── Loading guard ─────────────────────────────────────────────────────
    if (isLoading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Verifying admin access...</p>
                </div>
            </div>
        );
    }

    if (!isAuthorized) return null;

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Breadcrumb */}
                <nav className="flex mb-6" aria-label="Breadcrumb">
                    <ol className="inline-flex items-center space-x-1 md:space-x-3">
                        <li className="inline-flex items-center">
                            <Link to="/admin" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-red-600">
                                <FontAwesomeIcon icon="home" className="w-4 h-4 mr-2" />
                                Admin Dashboard
                            </Link>
                        </li>
                        <li>
                            <div className="flex items-center">
                                <FontAwesomeIcon icon="chevron-right" className="w-6 h-6 text-gray-400" />
                                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">Trophy Management</span>
                            </div>
                        </li>
                    </ol>
                </nav>

                {/* Header */}
                <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Trophy Management</h1>
                        <p className="mt-1 text-gray-500">Create, edit, and manage trophies and badges for users.</p>
                    </div>
                    <div className="flex gap-3">
                        <Link
                            to="/admin/users"
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 transition-colors"
                        >
                            <FontAwesomeIcon icon="users" className="mr-2" />
                            Manage Users
                        </Link>
                        <button
                            onClick={() => handleEditTrophy()}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                        >
                            <FontAwesomeIcon icon="plus" className="mr-2" />
                            New Trophy
                        </button>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="mb-6">
                        <ErrorMessage message={error} onRetry={() => setError(null)} />
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white border border-gray-200 shadow-sm rounded-lg mb-6">
                    <div className="px-5 py-4 border-b border-gray-100">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Filters</h2>
                    </div>
                    <div className="px-5 py-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Rarity</label>
                                <select
                                    value={filters.rarity}
                                    onChange={e => handleFilterChange('rarity', e.target.value)}
                                    className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                >
                                    <option value="">All Rarities</option>
                                    <option value="common">Common</option>
                                    <option value="rare">Rare</option>
                                    <option value="mythical">Mythical</option>
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                <input
                                    type="text"
                                    value={filters.search}
                                    onChange={e => handleFilterChange('search', e.target.value)}
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Search by name or description…"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trophies Grid */}
                <div className="bg-white border border-gray-200 shadow-sm rounded-lg">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Trophies</h2>
                        <span className="text-sm text-gray-500">
                            {trophies.length} of {pagination.total_trophies}
                        </span>
                    </div>

                    <div className="p-5">
                        {trophies.length === 0 ? (
                            <div className="text-center py-12">
                                <FontAwesomeIcon icon={faTrophy} className="mx-auto text-4xl text-gray-300 mb-3" />
                                <h3 className="text-sm font-medium text-gray-900">No trophies found</h3>
                                <p className="mt-1 text-sm text-gray-500">Get started by creating your first trophy.</p>
                                <div className="mt-6">
                                    <button
                                        onClick={() => handleEditTrophy()}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                                    >
                                        <FontAwesomeIcon icon="plus" className="mr-2" />
                                        Create Trophy
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {trophies.map(trophy => (
                                    <div key={trophy.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col">
                                        {/* Trophy header */}
                                        <div className="flex items-start gap-3 mb-3">
                                            <TrophyIcon trophy={trophy} size="md" />
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm font-semibold text-gray-900 truncate">{trophy.name}</h4>
                                                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border mt-0.5 ${getRarityColor(trophy.rarity)}`}>
                                                    {trophy.rarity}
                                                </span>
                                            </div>
                                        </div>

                                        <p className="text-xs text-gray-500 mb-3 line-clamp-2 flex-1">{trophy.description}</p>

                                        <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                                            <button
                                                onClick={() => handleViewRecipients(trophy)}
                                                className="text-xs text-gray-500 hover:text-red-600 underline underline-offset-2 transition-colors"
                                            >
                                                {trophy.user_count} {trophy.user_count === 1 ? 'user has' : 'users have'} this
                                            </button>
                                            <span>{formatDate(trophy.created_at)}</span>
                                        </div>

                                        {/* Action buttons */}
                                        <div className="flex items-center gap-1.5 pt-3 border-t border-gray-100">
                                            <button
                                                onClick={() => handleViewRecipients(trophy)}
                                                title="View Recipients"
                                                className="flex-1 inline-flex items-center justify-center py-1.5 text-sm text-gray-500 bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 transition-colors"
                                            >
                                                <FontAwesomeIcon icon="users" />
                                            </button>
                                            <button
                                                onClick={() => handleAssignTrophy(trophy)}
                                                title="Assign to User"
                                                className="flex-1 inline-flex items-center justify-center py-1.5 text-sm text-gray-500 bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 transition-colors"
                                            >
                                                <FontAwesomeIcon icon="user-plus" />
                                            </button>
                                            <button
                                                onClick={() => handleEditTrophy(trophy)}
                                                title="Edit Trophy"
                                                className="flex-1 inline-flex items-center justify-center py-1.5 text-sm text-gray-500 bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 transition-colors"
                                            >
                                                <FontAwesomeIcon icon="edit" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteTrophy(trophy.id)}
                                                title="Delete Trophy"
                                                className="flex-1 inline-flex items-center justify-center py-1.5 text-sm text-red-400 bg-white border border-red-100 rounded-md hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                                            >
                                                <FontAwesomeIcon icon="trash" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                                <p className="text-sm text-gray-500 hidden sm:block">
                                    Page {pagination.current_page} of {pagination.total_pages}
                                </p>
                                <div className="flex gap-2 mx-auto sm:mx-0">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, current_page: Math.max(1, prev.current_page - 1) }))}
                                        disabled={pagination.current_page === 1}
                                        className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        ‹ Prev
                                    </button>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, current_page: Math.min(prev.total_pages, prev.current_page + 1) }))}
                                        disabled={pagination.current_page === pagination.total_pages}
                                        className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Next ›
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Edit / Create Trophy Modal ──────────────────────────────────── */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                            <h3 className="text-base font-semibold text-gray-900">
                                {editingTrophy ? 'Edit Trophy' : 'Create New Trophy'}
                            </h3>
                            <button
                                onClick={() => setShowEditModal(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Scrollable fields */}
                        <form onSubmit={handleSaveTrophy} className="flex flex-col min-h-0 flex-1">
                            <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Trophy Name *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                        placeholder="Enter trophy name…"
                                        required
                                        maxLength={100}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                                        rows={3}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-none"
                                        placeholder="Describe what this trophy represents…"
                                        required
                                        maxLength={500}
                                    />
                                    <p className="mt-1 text-xs text-gray-400">{formData.description.length}/500</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Rarity</label>
                                    <select
                                        value={formData.rarity}
                                        onChange={e => setFormData(p => ({ ...p, rarity: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                    >
                                        <option value="common">Common</option>
                                        <option value="rare">Rare</option>
                                        <option value="mythical">Mythical</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Trophy Image</label>
                                    {formData.image_url && (
                                        <div className="flex items-center gap-3 mb-3">
                                            <img src={formData.image_url} alt="Preview" className="w-14 h-14 rounded-lg border object-contain" />
                                            <button
                                                type="button"
                                                onClick={() => setFormData(p => ({ ...p, image_url: '' }))}
                                                className="text-xs text-red-600 hover:text-red-800"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="w-full text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                                    />
                                    <p className="mt-1 text-xs text-gray-400">JPEG, PNG, GIF, WebP or SVG. Max 10 MB</p>
                                    <div className="mt-3">
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Or enter image URL</label>
                                        <input
                                            type="url"
                                            value={formData.image_url}
                                            onChange={e => setFormData(p => ({ ...p, image_url: e.target.value }))}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                            placeholder="https://…"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Fixed footer with action buttons */}
                            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setShowEditModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                                >
                                    {isSaving ? 'Saving…' : editingTrophy ? 'Update Trophy' : 'Create Trophy'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Assign Trophy Modal ──────────────────────────────────────────── */}
            {showAssignModal && assigningTrophy && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                            <h3 className="text-base font-semibold text-gray-900">
                                Assign "{assigningTrophy.name}"
                            </h3>
                            <button
                                onClick={() => setShowAssignModal(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-6 pt-4 flex-shrink-0">
                            <input
                                type="text"
                                value={userSearch}
                                onChange={e => setUserSearch(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                placeholder="Search users…"
                            />
                        </div>
                        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-2">
                            {filteredUsers.length === 0 ? (
                                <div className="text-center py-8 text-sm text-gray-400">No users found.</div>
                            ) : filteredUsers.map(u => (
                                <div key={u.user_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                            {u.profile_picture_url
                                                ? <img className="h-8 w-8 object-cover" src={u.profile_picture_url} alt="" />
                                                : <FontAwesomeIcon icon="user" className="text-gray-500 text-xs" />
                                            }
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{u.display_name || u.username}</p>
                                            <p className="text-xs text-gray-400">@{u.username} · {u.total_trophies} trophies</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleAssignTrophyToUser(u.user_id)}
                                        className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                                    >
                                        Assign
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {/* ── Recipients Modal ─────────────────────────────────────────────── */}
            {showRecipientsModal && recipientsTrophy && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <TrophyIcon trophy={recipientsTrophy} size="sm" />
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">
                                        "{recipientsTrophy.name}" Recipients
                                    </h3>
                                    <p className="text-xs text-gray-400 capitalize">{recipientsTrophy.rarity} trophy</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowRecipientsModal(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded flex-shrink-0"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-6 py-4 overflow-y-auto flex-1">
                            {isLoadingRecipients ? (
                                <div className="flex justify-center items-center py-12">
                                    <FontAwesomeIcon icon="spinner" className="animate-spin text-2xl text-gray-400" />
                                </div>
                            ) : recipients.length === 0 ? (
                                <div className="text-center py-12">
                                    <FontAwesomeIcon icon="users" className="mx-auto text-3xl text-gray-300 mb-3" />
                                    <p className="text-sm text-gray-500">No one has this trophy yet.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <p className="text-xs text-gray-400 mb-3">{recipients.length} {recipients.length === 1 ? 'user has' : 'users have'} this trophy</p>
                                    {recipients.map(r => (
                                        <div key={r.user_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                                            <div className="flex items-center gap-3">
                                                <div className="h-8 w-8 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                                    {r.profile_picture_url
                                                        ? <img className="h-8 w-8 object-cover" src={r.profile_picture_url} alt="" />
                                                        : <FontAwesomeIcon icon="user" className="text-gray-500 text-xs" />
                                                    }
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{r.display_name || r.username}</p>
                                                    <p className="text-xs text-gray-400">@{r.username}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-400">Awarded</p>
                                                <p className="text-xs text-gray-600">{new Date(r.awarded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTrophies;
